import React from 'react';
import { motion } from 'framer-motion';
import { ShoppingCart, Disc3, X, AlertCircle, CheckCircle2 } from 'lucide-react';
import { getTypeLabel, getTypeBadgeColor } from '../utils/badgeUtils';

export const PurchaseModal = ({
  selectedTrack,
  isOpen,
  onClose,
  onSubmit,
  isProcessing,
  purchaseSuccess,
  price,
  priceData,
  currentUser,
  priceError
}) => {
  if (!isOpen || !selectedTrack) return null;

  const userEmail = currentUser?.email || '';
  const userId = currentUser?.uid || '';

  const handleSubmit = (e) => {
    e.preventDefault();
    
    console.log('🛒 PurchaseModal.handleSubmit called');
    console.log('Email:', userEmail);
    console.log('UserId:', userId);
    console.log('SelectedTrack:', selectedTrack);
    console.log('PriceData:', priceData);
    
    // Validate email
    if (!userEmail) {
      alert('Please log in to your account to make a purchase.');
      return;
    }

    // Validate email format
    if (!/^\S+@\S+\.\S+$/.test(userEmail)) {
      alert('Please use a valid email address.');
      return;
    }

    // Validate priceData exists and has priceId
    if (!priceData || !priceData.priceId || !priceData.priceId.startsWith('pri_')) {
      alert('Invalid price configuration. Please contact support.');
      return;
    }

    // Pass userId as well
    console.log('✅ All validations passed, calling onSubmit');
    onSubmit(e, userEmail, userId);
  };

  // Calculate display price
  const displayPrice = parseFloat(price || 0).toFixed(2);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/80 backdrop-blur-md flex items-center justify-center z-50 p-3 sm:p-4 font-sans antialiased"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0, y: 20 }}
        animate={{ scale: 1, opacity: 1, y: 0 }}
        exit={{ scale: 0.9, opacity: 0, y: 20 }}
        transition={{ type: 'spring', damping: 25, stiffness: 300 }}
        className="bg-[#121318] border border-white/10 rounded-2xl shadow-2xl w-full max-w-sm sm:max-w-md p-5 sm:p-6 max-h-[90vh] overflow-y-auto text-white relative overflow-hidden"
        onClick={e => e.stopPropagation()}
      >
        {/* Ambient Gold Glow Background */}
        <div className="absolute -top-12 -right-12 w-32 h-32 bg-[#bd9f67]/15 rounded-full blur-2xl pointer-events-none" />

        {purchaseSuccess ? (
          /* Success State */
          <div className="text-center py-6 sm:py-8">
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: 'spring', damping: 15, stiffness: 150 }}
              className="w-16 h-16 rounded-full bg-[#bd9f67]/15 border border-[#bd9f67]/30 text-[#bd9f67] flex items-center justify-center mx-auto mb-4"
            >
              <CheckCircle2 size={36} />
            </motion.div>
            <h3 className="text-xl sm:text-2xl font-bold tracking-tight text-white mb-2">
              Payment Initiated!
            </h3>
            <p className="text-xs sm:text-sm text-white/70 mb-4 sm:mb-6 leading-relaxed">
              You've been redirected to Paddle to complete payment for <span className="font-semibold text-[#bd9f67]">{selectedTrack.name}</span>
            </p>
            <p className="text-xs text-white/40 mb-6">
              After payment, you'll receive your download link via email.
            </p>
            <button
              onClick={onClose}
              className="w-full px-4 py-3 bg-[#bd9f67] hover:bg-[#d0b07a] text-gray-950 font-bold rounded-xl transition duration-200 text-sm shadow-lg shadow-[#bd9f67]/20"
            >
              Close Window
            </button>
          </div>
        ) : (
          <>
            {/* Header */}
            <div className="flex items-start justify-between mb-4 sm:mb-5 pb-3 border-b border-white/10">
              <div className="flex-1 pr-2">
                <div className="flex items-center gap-1.5 text-[#bd9f67] text-[10px] font-semibold tracking-wider uppercase mb-1">
                  Digital Purchase
                </div>
                <h3 className="text-lg sm:text-xl font-bold tracking-tight text-white line-clamp-1">
                  {selectedTrack.name}
                </h3>
                <div className="flex flex-wrap items-center gap-2 mt-1.5">
                  <p className="text-xs text-white/60">
                    {selectedTrack.artist || 'The Asidors'}
                  </p>
                  <span
                    className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${getTypeBadgeColor(
                      selectedTrack.type || 'song'
                    )}`}
                  >
                    {getTypeLabel(selectedTrack.type || 'song')}
                  </span>
                  {selectedTrack.album && (
                    <span className="text-[11px] text-white/50 flex items-center gap-1">
                      <Disc3 size={12} />
                      {selectedTrack.album}
                    </span>
                  )}
                </div>
              </div>
              <button
                onClick={onClose}
                disabled={isProcessing}
                className="p-1.5 rounded-full text-white/40 hover:text-white hover:bg-white/10 transition disabled:opacity-50"
                aria-label="Close modal"
              >
                <X size={18} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Price Display Block */}
              <div className="bg-[#181a22] p-4 sm:p-5 rounded-xl border border-white/5 relative overflow-hidden">
                <p className="text-xs text-white/50 mb-1 font-medium uppercase tracking-wider">
                  Total Amount
                </p>
                <div className="flex items-baseline gap-2">
                  <p className="text-3xl sm:text-4xl font-extrabold text-[#bd9f67]">
                    ${displayPrice}
                  </p>
                  <p className="text-xs font-semibold text-white/40">USD</p>
                </div>
                {priceError && (
                  <motion.p 
                    initial={{ opacity: 0, y: -5 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="text-xs text-red-400 mt-2 flex items-center gap-1"
                  >
                    <AlertCircle size={14} />
                    {priceError}
                  </motion.p>
                )}
              </div>

              {/* Secure Payment Info Badge */}
              <div className="bg-white/5 border border-white/10 p-3 sm:p-3.5 rounded-xl">
                <div className="flex items-start gap-2.5">
                  <AlertCircle size={16} className="text-[#bd9f67] flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <p className="text-xs font-semibold text-white/90 mb-0.5">
                      Secure Checkout via Paddle
                    </p>
                    <p className="text-[11px] text-white/50 leading-relaxed">
                      Instant & encrypted transaction. Major credit/debit cards and PayPal accepted.
                    </p>
                  </div>
                </div>
              </div>

              {/* User Account Info */}
              {userEmail && (
                <div className="bg-black/30 p-3 rounded-xl border border-white/5">
                  <p className="text-[10px] text-white/40 font-semibold uppercase tracking-wider mb-0.5">Purchasing as</p>
                  <p className="text-xs font-medium text-white/90 break-all">
                    {userEmail}
                  </p>
                </div>
              )}

              {/* Login Alert */}
              {!userEmail && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-amber-500/10 border border-amber-500/20 p-3 rounded-xl flex items-start gap-2"
                >
                  <AlertCircle size={16} className="text-amber-400 flex-shrink-0 mt-0.5" />
                  <p className="text-xs text-amber-200">
                    Please sign in to your account to complete this digital purchase.
                  </p>
                </motion.div>
              )}

              {/* Action Buttons */}
              <div className="flex gap-2.5 pt-2">
                <button
                  type="button"
                  onClick={onClose}
                  disabled={isProcessing}
                  className="flex-1 px-4 py-3 text-xs sm:text-sm bg-white/5 hover:bg-white/10 text-white/70 border border-white/10 rounded-xl transition font-semibold disabled:opacity-50"
                >
                  Cancel
                </button>
                <motion.button
                  type="submit"
                  disabled={isProcessing || !userEmail}
                  whileTap={!isProcessing && userEmail ? { scale: 0.97 } : {}}
                  className="flex-1 px-4 py-3 text-xs sm:text-sm bg-[#bd9f67] hover:bg-[#d0b07a] text-gray-950 font-bold rounded-xl transition shadow-lg shadow-[#bd9f67]/20 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-[#bd9f67] flex items-center justify-center gap-2"
                >
                  <ShoppingCart size={15} />
                  <span>
                    {isProcessing ? 'Processing...' : 'Purchase'}
                  </span>
                </motion.button>
              </div>

              {/* Helper Footer Text */}
              <p className="text-[10px] text-center text-white/30 pt-1">
                Protected by Paddle checkout guardrails.
              </p>
            </form>
          </>
        )}
      </motion.div>
    </motion.div>
  );
};